var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import services from 'services';
import EmptyComponent from 'components/EmptyComponent';
import DataTable from 'library/components/DataTable';
import { Chip, monoColumn, Muted, mutedColumn, nameColumn, showColumn } from 'library/components/DataTable/cells';
import ListScreen, { ListAddButton, ListExportButton, listPager } from 'library/components/ListScreen';
import localeTranslator from 'library/Hocs/LocalTranslator';
import { StockMovement as StockMovementResource } from 'constants/resources';
import { downloadCsv, todayStamp } from '../csv';
import StockMovementDrawer from './StockMovementDrawer';
var textOf = function (value) { return (value == null ? '' : String(value)); };
var statusChipClass = function (status) {
    var value = status.toLowerCase();
    if (['cancel', 'reject', 'void'].some(function (part) { return value.includes(part); }))
        return 'is-inactive';
    if (['pending', 'draft', 'open'].some(function (part) { return value.includes(part); }))
        return 'is-amber';
    return 'is-active';
};
var StockMovements = function (props) {
    var t = props.translate;
    var navigate = useNavigate();
    var location = useLocation();
    var stockMovementService = services.stockMovementService, permissionService = services.permissionService;
    var canAdd = permissionService.hasPermissionToAdd(StockMovementResource);
    var canShow = permissionService.hasPermissionToShow(StockMovementResource);
    var _a = useState(1), pageNumber = _a[0], setPageNumber = _a[1];
    var _b = useState(''), searchValue = _b[0], setSearchValue = _b[1];
    var _c = useState(function () { var _a; return Boolean((_a = location.state) === null || _a === void 0 ? void 0 : _a.openNewStockMovement); }), drawerOpen = _c[0], setDrawerOpen = _c[1];
    var _d = useState(false), exporting = _d[0], setExporting = _d[1];
    var debounceRef = useRef(undefined);
    var _e = stockMovementService.useStockMovements(pageNumber), data = _e.data, isLoading = _e.isLoading;
    var movements = (data === null || data === void 0 ? void 0 : data.stockMovements) || [];
    var meta = data === null || data === void 0 ? void 0 : data.meta;
    var page = Number((meta === null || meta === void 0 ? void 0 : meta.page) || pageNumber);
    var pages = Math.max(1, Number((meta === null || meta === void 0 ? void 0 : meta.pages) || 1));
    var total = Number((meta === null || meta === void 0 ? void 0 : meta.count) || movements.length);
    var from = Number((meta === null || meta === void 0 ? void 0 : meta.from) || (movements.length ? 1 : 0));
    var to = Number((meta === null || meta === void 0 ? void 0 : meta.to) || movements.length);
    var visible = useMemo(function () {
        var query = searchValue.trim().toLowerCase();
        if (!query)
            return movements;
        return movements.filter(function (movement) {
            var _a, _b;
            var stock = movement.getStock();
            return [
                movement.getId(),
                (_a = stock === null || stock === void 0 ? void 0 : stock.getProduct()) === null || _a === void 0 ? void 0 : _a.getName(),
                (_b = stock === null || stock === void 0 ? void 0 : stock.getLocation()) === null || _b === void 0 ? void 0 : _b.getName(),
                movement.getDisplayedType(),
                movement.getDisplayedStatus(),
                movement.getQuantity(),
                movement.getTotalPrice()
            ]
                .join(' ')
                .toLowerCase()
                .includes(query);
        });
    }, [movements, searchValue]);
    var onSearchValueChange = function (event) {
        var value = event.target.value;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(function () { return setSearchValue(value); }, 300);
    };
    useEffect(function () {
        var state = location.state;
        if (!(state === null || state === void 0 ? void 0 : state.openNewStockMovement))
            return;
        setDrawerOpen(true);
        navigate('/stock_movements', { replace: true, state: null });
    }, [location.state, navigate]);
    var openMovement = useCallback(function (id) {
        if (!canShow)
            return;
        navigate("/stock_movements/".concat(id));
    }, [canShow, navigate]);
    var priceLabel = useCallback(function (movement) {
        return textOf(t('common:price', { amount: movement.getTotalPrice() }));
    }, [t]);
    var exportMovements = function () {
        if (exporting)
            return;
        if (!visible.length) {
            message.warning(t('exportEmpty'));
            return;
        }
        setExporting(true);
        try {
            var rows = __spreadArray([
                [t('id'), t('product'), t('location'), t('type'), t('status'), t('quantity'), t('totalPrice')]
            ], visible.map(function (movement) {
                var _a, _b;
                var stock = movement.getStock();
                return [
                    String(movement.getId()),
                    textOf((_a = stock === null || stock === void 0 ? void 0 : stock.getProduct()) === null || _a === void 0 ? void 0 : _a.getName()),
                    textOf((_b = stock === null || stock === void 0 ? void 0 : stock.getLocation()) === null || _b === void 0 ? void 0 : _b.getName()),
                    textOf(movement.getDisplayedType()),
                    textOf(movement.getDisplayedStatus()),
                    textOf(movement.getQuantity()),
                    priceLabel(movement)
                ];
            }), true);
            downloadCsv("stock-movements-page-".concat(page, "-").concat(todayStamp(), ".csv"), rows);
        }
        catch (_a) {
            message.error(t('exportFailed'));
        }
        finally {
            setExporting(false);
        }
    };
    var columns = useMemo(function () { return __spreadArray([
        monoColumn({ key: 'id', title: t('id'), width: '72px', variant: 'id', value: function (movement) { return movement.getId(); } }),
        nameColumn({ key: 'product', title: t('product'), width: 'minmax(160px, 1.3fr)', value: function (movement) { var _a, _b; return textOf((_b = (_a = movement.getStock()) === null || _a === void 0 ? void 0 : _a.getProduct()) === null || _b === void 0 ? void 0 : _b.getName()); } }),
        mutedColumn({ key: 'location', title: t('location'), width: 'minmax(140px, 1fr)', value: function (movement) { var _a, _b; return textOf((_b = (_a = movement.getStock()) === null || _a === void 0 ? void 0 : _a.getLocation()) === null || _b === void 0 ? void 0 : _b.getName()); } }),
        {
            key: 'type',
            title: t('type'),
            width: 'minmax(180px, 1.1fr)',
            render: function (movement) {
                var type = textOf(movement.getDisplayedType());
                return type ? _jsx(Chip, { tone: "is-sky", children: type }) : _jsx(Muted, {});
            }
        },
        {
            key: 'status',
            title: t('status'),
            width: '140px',
            render: function (movement) {
                var status = textOf(movement.getDisplayedStatus());
                return status ? _jsx(Chip, { tone: statusChipClass(textOf(movement.getStatus())), dot: true, children: status }) : _jsx(Muted, {});
            }
        },
        monoColumn({ key: 'qty', title: t('quantity'), width: '100px', value: function (movement) { return textOf(movement.getQuantity()); } }),
        monoColumn({ key: 'price', title: t('totalPrice'), width: '120px', value: function (movement) { return priceLabel(movement); } })
    ], (canShow ? [showColumn({
            title: t('action'),
            label: t('common:buttons.show'),
            onShow: function (movement) { return openMovement(movement.getId()); }
        })] : []), true); }, [t, priceLabel, canShow, openMovement]);
    return (_jsxs(ListScreen, { title: t('title'), count: t('countBadge', { count: total }), subtitle: t('subtitle'), actions: (_jsxs(_Fragment, { children: [_jsx(ListExportButton, { label: t('export'), busyLabel: t('exporting'), busy: exporting, onClick: exportMovements }), canAdd && _jsx(ListAddButton, { label: t('common:buttons.newStockMovement'), onClick: function () { return setDrawerOpen(true); } })] })), search: { placeholder: t('searchPlaceholder'), onChange: onSearchValueChange }, children: [_jsx(DataTable, { minWidth: "980px", loading: isLoading, rows: visible, rowKey: function (movement) { return movement.getId(); }, onRowClick: canShow ? function (movement) { return openMovement(movement.getId()); } : undefined, empty: _jsx(EmptyComponent, { description: t('empty') }), columns: columns, pager: listPager({
                    total: total,
                    page: page,
                    pages: pages,
                    meta: searchValue.trim() ? t('matchesOnPage', { count: visible.length }) : t('showingRange', { from: from, to: to, total: total }),
                    previous: t('previous'),
                    next: t('next'),
                    center: t('pageOf', { page: page, pages: pages }),
                    setPage: setPageNumber
                }) }), _jsx(StockMovementDrawer, { visiable: drawerOpen, onCloseDrawer: function () { return setDrawerOpen(false); } })] }));
};
export default localeTranslator(StockMovements, 'stockMovements');
